// Shared wallet simulation logic backed by localStorage.
// Used by React app. The standalone HTML pages (/tokens, /portfolio)
// duplicate a minimal version of this logic in /public/shared/wallet.js
// to remain framework-free.

const KEYS = {
  wallet: "stk_wallet",
  balances: "stk_balances",
  positions: "stk_positions",
  history: "stk_history",
  referral: "stk_referral",
  leaderboard: "stk_leaderboard",
};

const EVM_WALLETS = [
  { id: "metamask",      name: "MetaMask",        chain: "evm", color: "#F6851B" },
  { id: "walletconnect", name: "WalletConnect",   chain: "evm", color: "#3B99FC" },
  { id: "coinbase",      name: "Coinbase Wallet", chain: "evm", color: "#0052FF" },
];

const SOL_WALLETS = [
  { id: "phantom",  name: "Phantom",  chain: "solana", color: "#AB9FF2" },
  { id: "solflare", name: "Solflare", chain: "solana", color: "#FFB02E" },
  { id: "backpack", name: "Backpack", chain: "solana", color: "#E33E3F" },
];

export const ALL_WALLETS = [...EVM_WALLETS, ...SOL_WALLETS];

function read(k, fallback) {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function write(k, v) { localStorage.setItem(k, JSON.stringify(v)); }

function randHex(len) {
  const chars = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * 16)];
  return out;
}
function randBase58(len) {
  const chars = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export function getWallet() { return read(KEYS.wallet, null); }

function emitWalletChange() {
  try { window.dispatchEvent(new Event("stk-wallet-change")); } catch {}
}

export function shortAddr(addr) {
  if (!addr) return "";
  return addr.slice(0, 6) + "..." + addr.slice(-4);
}

export async function connectWallet(walletId, tokensSeed) {
  const w = ALL_WALLETS.find(x => x.id === walletId);
  if (!w) throw new Error("Unknown wallet");
  await new Promise(r => setTimeout(r, 900)); // simulate handshake
  const address = w.chain === "evm" ? "0x" + randHex(40) : randBase58(44);
  const wallet = { walletId: w.id, name: w.name, address, chain: w.chain, connectedAt: Date.now() };
  write(KEYS.wallet, wallet);

  // Seed balances if first time
  const balances = read(KEYS.balances, {});
  if (Object.keys(balances).length === 0 && tokensSeed) {
    const seeded = {};
    tokensSeed.forEach(t => {
      // Bigger random for meme/small tokens
      const base = t.minStake * (50 + Math.random() * 200);
      seeded[`${t.chain}:${t.symbol}`] = Math.round(base * 1e6) / 1e6;
    });
    write(KEYS.balances, seeded);
  }

  // Init referral if missing
  if (!read(KEYS.referral, null)) {
    write(KEYS.referral, {
      code: "STK-" + randHex(6).toUpperCase(),
      referrals: generateMockReferrals(),
    });
  }

  // Init leaderboard
  if (!read(KEYS.leaderboard, null)) {
    write(KEYS.leaderboard, generateMockLeaderboard());
  }

  emitWalletChange();
  return wallet;
}

export function disconnectWallet() {
  localStorage.removeItem(KEYS.wallet);
  emitWalletChange();
}

export function getBalances() { return read(KEYS.balances, {}); }
export function getBalance(symbol, chain) {
  return read(KEYS.balances, {})[`${chain}:${symbol}`] || 0;
}
export function setBalance(symbol, chain, amount) {
  const b = read(KEYS.balances, {});
  b[`${chain}:${symbol}`] = Math.max(0, amount);
  write(KEYS.balances, b);
}

export function getPositions() { return read(KEYS.positions, []); }
export function addPosition(p) {
  const list = read(KEYS.positions, []);
  list.unshift(p);
  write(KEYS.positions, list);
}
export function updatePosition(id, patch) {
  const list = read(KEYS.positions, []).map(p => p.id === id ? { ...p, ...patch } : p);
  write(KEYS.positions, list);
}
export function removePosition(id) {
  write(KEYS.positions, read(KEYS.positions, []).filter(p => p.id !== id));
}

export function getHistory() { return read(KEYS.history, []); }
export function pushHistory(tx) {
  const list = read(KEYS.history, []);
  list.unshift(tx);
  write(KEYS.history, list.slice(0, 200));
}

export function getReferral() { return read(KEYS.referral, null); }
export function getLeaderboard() { return read(KEYS.leaderboard, []); }

/* Virtual clock for demo mode. When demo is active (set via standalone page MAX click),
   elapsed time after activation is multiplied by `factor`. Used by calcPending so React
   Profile and standalone page stay in sync. */
export function vNow() {
  try {
    const raw = localStorage.getItem("stk_demo_boost");
    if (!raw) return Date.now();
    const d = JSON.parse(raw);
    if (!d || !d.activatedAt) return Date.now();
    return d.activatedAt + (Date.now() - d.activatedAt) * (d.factor || 480);
  } catch { return Date.now(); }
}

export function isBoostDemoActive() {
  try {
    const raw = localStorage.getItem("stk_demo_boost");
    return !!(raw && JSON.parse(raw)?.activatedAt);
  } catch { return false; }
}

// Calculate live pending rewards for a position based on APR + auto-compounding (continuous).
// Honours the global virtual clock (demo mode) for accelerated time.
export function calcPending(p, now = vNow()) {
  const last = p.lastClaim || p.startedAt;
  const seconds = Math.max(0, (now - last) / 1000);
  const years = seconds / (365 * 24 * 3600);
  const r = p.apr / 100;
  return p.amount * (Math.exp(r * years) - 1);
}

export function fmtNum(n, decimals = 4) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3 && decimals <= 2) return (n / 1e3).toFixed(2) + "K";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: decimals });
}

export function fmtAmount(n) {
  if (n === 0) return "0";
  if (n < 0.0001) return n.toExponential(2);
  if (n < 1) return n.toFixed(6);
  if (n < 100) return n.toFixed(4);
  return n.toFixed(2);
}

function generateMockReferrals() {
  const names = ["whale_0x12", "deg3n_alpha", "validator.eth", "sol_max", "moonboy", "ape_in_3000", "yield_hunter"];
  return names.slice(0, 3 + Math.floor(Math.random() * 3)).map((n, i) => ({
    handle: n,
    joinedAt: Date.now() - (i + 1) * 86400000 * (2 + Math.random() * 10),
    totalStaked: Math.round(500 + Math.random() * 8500),
    earned: Math.round(20 + Math.random() * 240),
  }));
}

function generateMockLeaderboard() {
  const handles = [
    "0xWhaleKing", "validator.eth", "sol.maxi", "deg3n_alpha", "yield.king",
    "mev_searcher", "stakemaster", "polygon.legend", "ape_in_3000", "the.fund",
    "node.runner", "compound.chad", "lp.ninja", "0xAnonStaker", "based_validator",
  ];
  return handles.map((h, i) => ({
    rank: i + 1,
    handle: h,
    totalStaked: Math.round(2500000 / (i + 1) + Math.random() * 100000),
    earned:      Math.round(  85000 / (i + 1) + Math.random() *   8000),
    chain: i % 3 === 0 ? "solana" : "evm",
  }));
}

export function txHash(chain) {
  return chain === "evm" ? "0x" + randHex(64) : randBase58(88);
}
